'use client';

import React, { ReactNode } from 'react';

import { cn } from '@/lib/utils';

import { WithRef } from '@/components/types';

import { HTMLDataTrackerProps } from '@/app/types';
import Button from '@/decodo/components/atoms/Button';
import { returnRefAndGenerateDataTracker } from '@/decodo/components/atoms/Button/utils';
import { InputLabel } from '@/decodo/components/atoms/Input/InputLabel';
import { Text } from '@/decodo/components/atoms/Text';
import { safariSvgFix } from '@/utils/browsers';

import IconInfo from '~/svg/IconInfo.svg';
import IconSearch from '~/svg/IconSearch.svg';

// NOTE: sharedStyles is used in both input and button components, when moving to Decodo check file stylesDC.ts
export const sharedStyles = cn(
  'bg-base-white hover:border-gray-500 font-normal disabled:bg-gray-50 hover:disabled:border-gray-300 placeholder-gray-500 text-gray-950 border-gray-300 rounded border font-mono'
);

const commonStyles = cn('w-full inline-flex gap-2 ', sharedStyles);
const textareaStyles = 'px-4 pt-2.5 pb-8 min-h-[120px] resize-none';

const heights = {
  m: 'h-[38px]',
  s: 'h-[30px]',
} as const;

const sizes = {
  m: 'text-body-m',
  s: 'text-body-m',
} as const;

const iconLeftPaddings = {
  m: 'px-4',
  s: 'pl-4 pr-2 py-1',
} as const;

type ClassNameConfig = {
  root?: string;
  disabled?: string;
  error?: string;
};
type ConfigurableClassNameElement = 'input' | 'label' | 'bottomText';
type StyleCollection = { [K in ConfigurableClassNameElement]: string };

type BaseInputProps = {
  classNames?: {
    root?: string;
    leftIcon?: string;
    rightIcon?: string;
    search?: string;
    input?: ClassNameConfig & {
      inputElement?: string;
    };
    label?: ClassNameConfig;
    bottomText?: ClassNameConfig;
  };
  isSearch?: boolean;
  size?: keyof typeof sizes;
  bottomText?: string;
  name: string;
  label?: ReactNode;
  error?: string;
  hasBottomHelpIcon?: boolean;
  isDisabled?: boolean;
  isRequired?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
} & HTMLDataTrackerProps;

type InputSpecificProps = BaseInputProps & {
  as?: 'input';
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
} & Omit<
    React.InputHTMLAttributes<HTMLInputElement>,
    'size' | 'onChange' | 'className' | 'disabled'
  >;

type TextareaSpecificProps = BaseInputProps & {
  as: 'textarea';
  onChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
} & Omit<
    React.TextareaHTMLAttributes<HTMLTextAreaElement>,
    'size' | 'onChange' | 'className' | 'disabled'
  >;

export type InputFieldProps = InputSpecificProps | TextareaSpecificProps;

export const Input = <T extends InputFieldProps>({
  classNames,
  name,
  label,
  isSearch,
  size = 'm',
  bottomText,
  error,
  hasBottomHelpIcon,
  isDisabled,
  isRequired,
  leftIcon,
  rightIcon,
  onChange,
  as = 'input',
  ref,
  ...props
}: WithRef<
  T extends InputSpecificProps ? HTMLInputElement : HTMLTextAreaElement,
  T
>) => {
  const bothInputsStyles = cn(
    'w-full rounded',
    isSearch || rightIcon ? 'pl-4 pr-12' : 'px-4',
    !!leftIcon && 'pl-10 pr-4',
    'focus-within:shadow-border-px focus-within:shadow-brand-500 outline-brand-500 focus:outline-none',
    classNames?.input?.inputElement
  );
  const combinedRootClasses = cn(commonStyles, sizes[size]);

  const stdStyles: StyleCollection = {
    input: cn(
      combinedRootClasses,
      isSearch ? 'rounded-e-md' : 'rounded',
      classNames?.input?.root
    ),
    label: cn('text-gray-950 block', classNames?.label?.root),
    bottomText: cn(
      'text-body-s pointer-events-none flex h-4 gap-x-1 text-gray-950 dark:text-gray-dark-50',
      classNames?.bottomText?.root
    ),
  };

  const errorStyles: StyleCollection = {
    input: cn(
      'border-error-500 dark:border-error-500',
      classNames?.input?.error
    ),
    label: cn('text-error-500 dark:text-error-500', classNames?.label?.error),
    bottomText: cn(
      'text-error-500 dark:text-error-500',
      classNames?.bottomText?.error
    ),
  };

  const disabledStyles: StyleCollection = {
    input: cn(
      'hover:border-gray-300 border-gray-300 bg-gray-50',
      classNames?.input?.disabled
    ),
    label: classNames?.label?.disabled || '',
    bottomText: cn('text-gray-500', classNames?.bottomText?.disabled),
  };

  const getPrioritisedStyles = (
    element: ConfigurableClassNameElement
  ): string =>
    cn(
      stdStyles[element],
      isDisabled && disabledStyles[element],
      error && errorStyles[element]
    );

  return (
    <div className={cn('relative font-mono', classNames?.root)}>
      {label && (
        <InputLabel
          fieldName={name}
          label={label}
          required={isRequired}
          classNames={{
            label: cn(getPrioritisedStyles('label'), classNames?.label),
          }}
        />
      )}
      <div
        className={cn(
          'relative flex flex-col items-start justify-end',
          getPrioritisedStyles('input'),
          'focus-within:!border-brand-500',
          'focus-within:outline-1',
          as === 'textarea' && 'focus-within:ring-brand-500 focus-within:ring-1'
        )}
      >
        {leftIcon && (
          <div
            className={cn(
              'pointer-events-none absolute grid h-10 w-10 items-center',
              heights[size],
              iconLeftPaddings[size],
              classNames?.leftIcon
            )}
          >
            {leftIcon}
          </div>
        )}
        {as === 'input' && (
          <input
            disabled={isDisabled}
            type='text'
            onChange={onChange as React.ChangeEventHandler<HTMLInputElement>}
            name={name}
            className={cn(heights[size], bothInputsStyles)}
            data-tracker-type='input'
            data-tracker-context={name}
            ref={(_ref) => {
              returnRefAndGenerateDataTracker(_ref, ref);
            }}
            {...(props as Omit<
              React.InputHTMLAttributes<HTMLInputElement>,
              'size' | 'onChange' | 'className' | 'disabled'
            >)}
          />
        )}
        {as === 'textarea' && (
          <>
            <textarea
              disabled={isDisabled}
              onChange={
                onChange as React.ChangeEventHandler<HTMLTextAreaElement>
              }
              name={name}
              className={cn(
                textareaStyles,
                bothInputsStyles,
                'outline-0 focus-within:shadow-none'
              )}
              data-tracker-type='textarea'
              data-tracker-context={name}
              ref={(_ref) => {
                returnRefAndGenerateDataTracker(_ref, ref);
              }}
              {...(props as Omit<
                React.TextareaHTMLAttributes<HTMLTextAreaElement>,
                'size' | 'onChange' | 'className' | 'disabled'
              >)}
            />
            {!!props.maxLength && (
              <Text
                variant='body-m'
                color='gray-500'
                classNames={{ wrapper: 'flex self-end px-4 pb-2.5' }}
              >
                {(props.value as string)?.length} / {props.maxLength}
              </Text>
            )}
          </>
        )}
        {as === 'input' && isSearch && (
          <Button
            variant='text'
            data-tracker-component='search-button'
            className={cn(
              'text-base-white absolute end-0 grid h-10 w-10 rounded-e px-3',
              size === 's' ? 'py-2' : 'py-2.5',
              heights[size],
              isDisabled ? 'bg-gray-300' : 'bg-gray-950',
              classNames?.search
            )}
          >
            <IconSearch
              {...safariSvgFix({ size: 16 })}
              className={classNames?.rightIcon}
            />
          </Button>
        )}
        {rightIcon && (
          <div
            className={cn(
              'pointer-events-none absolute end-0 grid h-4 w-4 pl-2 pr-8',
              heights[size],
              classNames?.rightIcon
            )}
          >
            {rightIcon}
          </div>
        )}
      </div>
      <div
        data-tracker-input-bottom={name}
        {...(error && { 'data-tracker-error': error })}
        className={getPrioritisedStyles('bottomText')}
      >
        {hasBottomHelpIcon && <IconInfo />}
        {error || bottomText}
      </div>
    </div>
  );
};
